/**
 * FusionCanvasStage — Workbench-style graph stage: palette + canvas + collapsible
 * inspector. Canvas owns ~80%+ of the row when the inspector is minimized (default);
 * selecting a node expands the inspector, matching crypto Workbench behaviour.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { CanvasPalette } from './CanvasPalette';
import { EdgeCollapseTab } from '../workbench/EdgeCollapseTab';
import { useFusionStore } from '../../stores/useFusionStore';

const INSPECTOR_KEY = 'fusion-inspector-minimized';
/** Expanded inspector width — spec §7 says 340px; keep readable without stealing the canvas. */
const INSPECTOR_WIDTH = 340;
const INSPECTOR_MINIMIZED = 44;

function readMinimized(): boolean {
  try {
    const v = localStorage.getItem(INSPECTOR_KEY);
    // Default minimized so the canvas dominates on first paint (Workbench parity).
    if (v === null) return true;
    return v === '1';
  } catch {
    return true;
  }
}

export function FusionCanvasStage({
  children,
  aside,
  forceAsideExpanded = false,
}: {
  children: ReactNode;
  aside: ReactNode;
  /** One-shot expand when Bank import / Court-safe / Case files opens — does not lock open. */
  forceAsideExpanded?: boolean;
}) {
  const selected = useFusionStore((s) => s.selected);
  const [minimized, setMinimized] = useState(readMinimized);

  // Node click → expand inspector (same contract as Workbench Node Inspector).
  useEffect(() => {
    if (selected) setMinimized(false);
  }, [selected]);

  // Rising-edge expand only: officer can still collapse while staying on import/ledger.
  useEffect(() => {
    if (forceAsideExpanded) setMinimized(false);
  }, [forceAsideExpanded]);

  const collapsed = minimized;
  const toggle = () => {
    setMinimized((m) => {
      const next = !m;
      try { localStorage.setItem(INSPECTOR_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div
      className="flex-1 min-h-0 relative z-0 bg-canvas overflow-hidden"
      style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto' }}
      data-testid="fusion-graph-area"
    >
      <CanvasPalette />

      <div className="relative min-w-0 min-h-0 h-full overflow-hidden bg-canvas">
        {/* Same Workbench dotted grid — theme tokens from index.css */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none z-0"
          data-testid="fusion-canvas-grid"
          style={{
            backgroundColor: 'hsl(var(--canvas-bg))',
            backgroundImage: 'radial-gradient(circle, hsl(var(--canvas-grid-dot)) 1px, transparent 1px)',
            backgroundSize: 'var(--canvas-grid-size) var(--canvas-grid-size)',
          }}
        />
        <div className="absolute inset-0 z-[1] min-h-0 overflow-hidden">
          {children}
        </div>
      </div>

      <aside
        className="relative bg-card border-l border-border z-10 flex flex-col min-h-0 transition-[width] duration-200 ease-in-out"
        style={{ width: collapsed ? INSPECTOR_MINIMIZED : INSPECTOR_WIDTH }}
        data-testid="fusion-inspector-aside"
        data-minimized={collapsed ? 'true' : 'false'}
      >
        <EdgeCollapseTab
          side="left"
          direction={collapsed ? 'expand' : 'collapse'}
          onClick={toggle}
          label={collapsed ? 'Expand inspector' : 'Collapse inspector'}
        />

        <div
          className={clsx(
            'absolute inset-0 flex flex-col items-center py-2 gap-1',
            collapsed ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
          aria-hidden={!collapsed}
        >
          <div className="mt-2 text-[10px] font-medium text-muted-foreground tracking-wide select-none [writing-mode:vertical-rl] rotate-180">
            Inspector
          </div>
        </div>

        <div
          className={clsx('flex flex-col flex-1 min-h-0 overflow-hidden', collapsed && 'hidden')}
          aria-hidden={collapsed}
        >
          {aside}
        </div>
      </aside>
    </div>
  );
}
